import { useDataValue, useDataChange, DataProvider } from 'Simple/Data.js'
import ActionsAction from './ActionsAction/index.js'
import AssignedTo from './AssignedTo/index.js'
import CommentsLogs from './CommentsLogs/index.js'
import Priority from './Priority/index.js'
import DueDate from './DueDate/index.js'
import TaskOverview from './TaskOverview/index.js'
import SelectAction from './SelectAction/index.js'
import SelectAllAction from './SelectAllAction/index.js'
import StageLane from './StageLane/index.js'
import React, { useMemo } from 'react'

import View from './view.js'

export default function Logic(props) {
  let tasks = useDataValue({
    context: 'tasks',
    path: 'tasks_tasks',
    viewPath: props.viewPath,
  })
  let stages = useDataValue({
    context: 'tasks',
    path: 'tasks_stages',
    viewPath: props.viewPath,
  })

  let change = useDataChange({
    context: 'selected',
    path: 'bulk_task_ids',
    viewPath: props.viewPath,
  })

  let collapsedStageLanes = useDataValue({
    context: 'collapsed_stage_lanes',
    viewPath: props.viewPath,
  })

  let value = useMemo(() => {
    let columns = [
      {
        id: 'select-col',
        width: '14px',
        header: ({ table }) => (
          <SelectAllAction
            value={table.getIsAllRowsSelected()}
            onChange={() =>
              table.toggleAllRowsSelected(!table.getIsAllRowsSelected())
            }
          />
        ),
        enableSorting: false,
        enableColumnFilter: false,
        accessorKey: 'select',
        colSpan: ({ row }) => (row.original.type === 'stage' ? 7 : 1),
        cell: ({ cell, row }) => {
          if (row.original.type === 'stage') {
            return (
              <DataProvider
                context="stage"
                value={row.original}
                viewPath={props.viewPath}
              >
                <StageLane
                  viewPath={`${props.viewPath}/StageLane(${cell.row.original.id})`}
                />
              </DataProvider>
            )
          }

          return (
            <SelectAction
              viewPath={`${props.viewPath}/SelectAction(${cell.row.original.id})`}
              value={row.getIsSelected()}
              onChange={row.getToggleSelectedHandler()}
            />
          )
        },
      },
      {
        id: 'task',
        header: () => <div style={{ textAlign: 'left' }}>Patient / Task</div>,
        enableSorting: false,
        enableColumnFilter: false,
        accessorKey: 'employee_name',
        cell: ({ cell, row }) => {
          if (row.original.type === 'stage') return null

          return (
            <DataProvider
              context="task"
              value={cell.row.original}
              viewPath={props.viewPath}
            >
              <TaskOverview
                viewPath={`${props.viewPath}/TaskOverview(${cell.row.original.id})`}
              />
            </DataProvider>
          )
        },
      },
      {
        id: 'assignee',
        header: () => <div style={{ textAlign: 'left' }}>Assigned to</div>,
        enableSorting: false,
        enableColumnFilter: false,
        accessorKey: 'user_id',
        cell: ({ cell, row }) => {
          if (row.original.type === 'stage') return null
          return (
            <DataProvider
              context="task"
              value={cell.row.original}
              viewPath={props.viewPath}
            >
              <AssignedTo
                viewPath={`${props.viewPath}/AssignedTo(${cell.row.original.id})`}
              />
            </DataProvider>
          )
        },
      },
      {
        id: 'due',
        width: '100px',
        header: () => <div style={{ textAlign: 'left' }}>Due date</div>,
        enableSorting: false,
        enableColumnFilter: false,
        accessorKey: 'due_date',
        cell: ({ cell, row }) => {
          if (row.original.type === 'stage') return null
          return (
            <DataProvider
              context="task"
              value={cell.row.original}
              viewPath={props.viewPath}
            >
              <DueDate
                viewPath={`${props.viewPath}/DueDate(${cell.row.original.id})`}
              />
            </DataProvider>
          )
        },
      },
      {
        id: 'priority',
        header: () => <div style={{ textAlign: 'left' }}>Priority</div>,
        enableSorting: false,
        enableColumnFilter: false,
        accessorKey: 'priority',
        cell: ({ cell, row }) => {
          if (row.original.type === 'stage') return null
          return (
            <DataProvider
              context="task"
              value={cell.row.original}
              viewPath={props.viewPath}
            >
              <Priority
                viewPath={`${props.viewPath}/Priority(${cell.row.original.id})`}
              />
            </DataProvider>
          )
        },
      },
      {
        id: 'comments',
        header: () => <div style={{ textAlign: 'center' }}>Comments & Log</div>,
        enableSorting: false,
        enableColumnFilter: false,
        accessorKey: 'comments_count',
        cell: ({ cell, row }) => {
          if (row.original.type === 'stage') return null
          return (
            <DataProvider
              context="task"
              value={cell.row.original}
              viewPath={props.viewPath}
            >
              <CommentsLogs
                viewPath={`${props.viewPath}/CommentsLogs(${cell.row.original.id})`}
              />
            </DataProvider>
          )
        },
      },
      {
        id: 'actions',
        header: () => <div style={{ textAlign: 'center' }}>Actions</div>,
        enableSorting: false,
        enableColumnFilter: false,
        accessorKey: 'stages',
        cell: ({ cell, row }) => {
          if (row.original.type === 'stage') return null
          return (
            <DataProvider
              context="task"
              value={cell.row.original}
              viewPath={props.viewPath}
            >
              <ActionsAction
                viewPath={`${props.viewPath}/ActionsAction(${cell.row.original.id})`}
              />
            </DataProvider>
          )
        },
      },
    ].filter(Boolean)

    if (!tasks) return { columns, data: [] }

    let stagesAndTasks = []
    for (let stage of stages) {
      let stageTasks = tasks.filter(task => task.stage_id === stage.id)

      if (stageTasks.length > 0) {
        stagesAndTasks.push({
          type: 'stage',
          id: stage.id,
          name: stage.name,
          done: stage.done,
        })

        stagesAndTasks.push(
          ...stageTasks
            .filter(task => !collapsedStageLanes.includes(task.stage_id))
            .map(task => ({
              type: 'task',
              ...task,
            }))
        )
      }
    }

    return {
      columns,
      data: stagesAndTasks,
    }
  }, [tasks, collapsedStageLanes]) //eslint-disable-line

  function onChange(table) {
    let selected = table.getSelectedRowModel().rows.map(r => r.original.id)
    change(selected)
  }

  return <View value={value} onChange={onChange} viewPath={props.viewPath} />
}
